import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { apiCall } from '../utils/api';

/**
 * Free US address autocomplete input (Photon/OpenStreetMap via backend).
 *
 * Props:
 *   value / onChange(text)     — controlled text input
 *   onSelect(suggestion)       — {label, street, city, state, zip, lat, lng}
 *   placeholder, className, disabled, required, testId
 */
export default function AddressAutocomplete({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Start typing a US address…',
  className = '',
  disabled = false,
  required = false,
  testId = 'address-autocomplete',
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);
  const skipNextFetch = useRef(false);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const fetchSuggestions = async (q) => {
    setLoading(true);
    try {
      const res = await apiCall(`/api/address/autocomplete?q=${encodeURIComponent(q)}`);
      const list = res?.suggestions || [];
      setSuggestions(list);
      setOpen(list.length > 0);
    } catch (_e) {
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e) => {
    const text = e.target.value;
    onChange?.(text);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (!text || text.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(() => fetchSuggestions(text.trim()), 350);
  };

  const handlePick = (s) => {
    skipNextFetch.current = true;
    onChange?.(s.street || s.label);
    onSelect?.(s);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={handleInput}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        autoComplete="off"
        className={className}
        data-testid={`${testId}-input`}
      />
      {loading && (
        <Loader2 className="w-4 h-4 animate-spin text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      )}
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-[80] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto py-1"
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((s, idx) => (
            <li key={`${s.label}-${idx}`}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handlePick(s)}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-start gap-2"
                data-testid={`${testId}-suggestion-${idx}`}
              >
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <span>{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
